/* eslint-disable max-lines */
import { useState } from 'react';
import moment from 'moment';
import { toast } from 'react-toastify';
import StepShell from '../StepShell';
import SignatureBottomSheet from '../components/SignatureBottomSheet';
import InlinePdfViewer from '../components/InlinePdfViewer';
import { colors, layout } from '../styles';
import HireDocuments from '../../../../../services/HireDocuments';
import { getCurrentW4Config, W4_CURRENT_FORM_YEAR } from '../../../../../helpers/w4FormConfig';
import { buildW4Pdf } from '../utils/pdfBuilders';
import { useLivePdfPreview } from '../utils/livePdfPreview';
import { getPreEmploymentLookup } from '../utils/preEmploymentLookup';
import { sanitizeAmountInput } from '../utils/numberValidation';
import DateInput from '../components/DateInput';
import { BaseStepProps } from './BaseStepProps';

import { selectAllOnFocus } from '../../../../../helpers/selectOnFocus';
type AmountEntry = { i: number; v: number };

interface W4Draft {
  firstName: string;
  lastName: string;
  ssn: string;
  address: string;
  city: string;
  married: '' | '1' | '2' | '3';
  amounts: AmountEntry[];
  date: string;
}

const emptyDraft: W4Draft = {
  firstName: '',
  lastName: '',
  ssn: '',
  address: '',
  city: '',
  married: '',
  amounts: [],
  date: '',
};

const formatSsn = (raw: string): string => {
  const digits = raw.replace(/\D/g, '').slice(0, 9);
  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
};

const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result));
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const setAmount = (amounts: AmountEntry[], i: number, v: number): AmountEntry[] => {
  const next = amounts.filter((a) => a.i !== i);
  if (!Number.isNaN(v)) next.push({ i, v });
  return next;
};

const getAmount = (amounts: AmountEntry[], i: number): string => {
  const found = amounts.find((a) => a.i === i);
  return found?.v != null ? String(found.v) : '';
};

const filingOptions: { value: '1' | '2' | '3'; label: string }[] = [
  { value: '1', label: 'Single or Married filing separately' },
  { value: '2', label: 'Married filing jointly or Qualifying surviving spouse' },
  { value: '3', label: 'Head of household' },
];

const childAmountLabel =
  W4_CURRENT_FORM_YEAR >= 2026
    ? 'Step 3 — Multiply qualifying children under age 17 by $2,200'
    : 'Step 3 — Multiply qualifying children under age 17 by $2,000';

const W4Step = ({
  step,
  stepIndex,
  totalSteps,
  completedSteps,
  token,
  tokenData,
  application,
  applicationDraft,
  draftDocs,
  draft,
  saveDraft,
  clearDraft,
  onBack,
  onPrevious,
  onNext,
  onSaveLater,
}: BaseStepProps) => {
  const backendDraft = draftDocs.find((d) => d.type === 'W4');
  const backendFields = (backendDraft?.options?.fields || {}) as Partial<W4Draft>;
  const lookup = getPreEmploymentLookup(application, applicationDraft, token);
  const initialFromBackend: W4Draft = {
    ...emptyDraft,
    firstName: lookup.firstName,
    lastName: lookup.lastName,
    ssn: lookup.ssn,
    address: lookup.address,
    city: [lookup.city, lookup.state, lookup.zipCode].filter(Boolean).join(', '),
    ...backendFields,
  };
  const initial = (draft as W4Draft) || initialFromBackend;
  const [data, setData] = useState<W4Draft>({ ...emptyDraft, ...initial });
  const [signatureOpen, setSignatureOpen] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);

  const config = getCurrentW4Config();
  const employerName = tokenData.school?.name || '';
  const ein = tokenData.school?.federalTaxId || '';

  const update = (patch: Partial<W4Draft>) => {
    const next = { ...data, ...patch };
    setData(next);
    saveDraft(next);
  };

  const updateAmount = (i: number, rawValue: string) => {
    const sanitized = sanitizeAmountInput(rawValue, { decimalScale: 2 });
    const num = sanitized === '' ? NaN : Number(sanitized);
    update({ amounts: setAmount(data.amounts, i, num) });
  };

  const canOpenSignature =
    !!data.firstName &&
    !!data.lastName &&
    !!data.ssn &&
    !!data.address &&
    !!data.city &&
    !!data.married &&
    !savingDraft;

  const buildFieldsPayload = () => ({
    firstName: data.firstName,
    lastName: data.lastName,
    ssn: data.ssn,
    address: data.address,
    city: data.city,
    married: data.married,
    amounts: data.amounts,
    employerName,
    ein,
    date: data.date,
  });

  const livePdfUrl = useLivePdfPreview({
    builder: () =>
      buildW4Pdf({
        firstName: data.firstName,
        lastName: data.lastName,
        ssn: data.ssn,
        address: data.address,
        city: data.city,
        married: data.married || undefined,
        amounts: data.amounts,
        employerName,
        ein,
        date: data.date,
      }),
    deps: [data, employerName, ein],
  });

  const submitWithSignature = async (signaturePng: string) => {
    const pdfBytes = await buildW4Pdf(
      {
        firstName: data.firstName,
        lastName: data.lastName,
        ssn: data.ssn,
        address: data.address,
        city: data.city,
        married: data.married || undefined,
        amounts: data.amounts,
        employerName,
        ein,
        date: data.date,
      },
      signaturePng,
      moment().format('YYYY-MM-DD'),
    );
    const pdfSigned = await blobToBase64(new Blob([pdfBytes as any], { type: 'application/pdf' }));

    await HireDocuments('').create({
      type: 'W4',
      userId: tokenData.userId,
      userName: tokenData.userName,
      userToken: token,
      pdfUrl: config.pdfUrl,
      pdfSigned,
      signature: signaturePng,
      isDraft: false,
      options: {
        type: 'W4',
        formYear: W4_CURRENT_FORM_YEAR,
        fields: buildFieldsPayload(),
        coordinatesSign: config.coordinatesSign,
        coordinatesDate: config.coordinatesDate,
        signPng: signaturePng,
        signatureDate: moment().format('YYYY-MM-DD'),
      },
    });

    clearDraft();
    toast.success('W-4 signed.');
    setSignatureOpen(false);
    onNext();
  };

  const handleSaveLater = async () => {
    if (savingDraft) return;
    setSavingDraft(true);
    try {
      await HireDocuments('').create({
        type: 'W4',
        userId: tokenData.userId,
        userName: tokenData.userName,
        userToken: token,
        pdfUrl: config.pdfUrl,
        pdfSigned: null,
        signature: '',
        isDraft: true,
        options: {
          type: 'W4',
          formYear: W4_CURRENT_FORM_YEAR,
          fields: buildFieldsPayload(),
          coordinatesSign: config.coordinatesSign,
          coordinatesDate: config.coordinatesDate,
          signPng: '',
        },
      });
      clearDraft();
      toast.success('Progress saved.');
      onSaveLater();
    } catch (err: any) {
      console.error(err);
      toast.error(err?.response?.data?.message || 'Could not save W-4.');
    } finally {
      setSavingDraft(false);
    }
  };

  const inputRow = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 };

  return (
    <StepShell
      stepNumber={stepIndex + 1}
      totalSteps={totalSteps}
      completedSteps={completedSteps}
      title={step.title}
      onBack={onBack}
      onPrevious={onPrevious}
      onSaveLater={handleSaveLater}
      onNext={() => setSignatureOpen(true)}
      nextLabel="Accept & Sign"
      nextDisabled={!canOpenSignature}
    >
      <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Form W-4: Employee&apos;s Withholding Certificate</h3>
      <p style={{ color: colors.muted, fontSize: 14, marginTop: 6 }}>
        This form determines how much federal income tax is withheld from your paycheck. Review the form below, then
        complete the fields and sign at the bottom.
      </p>

      <InlinePdfViewer url={livePdfUrl || config.pdfUrl} maxHeight="55vh" />

      <h4 style={{ fontSize: 15, fontWeight: 700, margin: '16px 0 10px' }}>Step 1 — Personal Information</h4>

      <div style={inputRow}>
        <div>
          <label style={layout.label}>First name and middle initial *</label>
          <input
            type="text"
            value={data.firstName}
            onChange={(e) => update({ firstName: e.target.value })}
            style={layout.input}
          />
        </div>
        <div>
          <label style={layout.label}>Last name *</label>
          <input
            type="text"
            value={data.lastName}
            onChange={(e) => update({ lastName: e.target.value })}
            style={layout.input}
          />
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={layout.label}>Social Security Number *</label>
        <input
          type="text"
          value={data.ssn}
          onChange={(e) => update({ ssn: formatSsn(e.target.value) })}
          placeholder="XXX-XX-XXXX"
          style={layout.input}
        />
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={layout.label}>Address *</label>
        <input
          type="text"
          value={data.address}
          onChange={(e) => update({ address: e.target.value })}
          style={layout.input}
        />
      </div>

      <div style={{ marginBottom: 20 }}>
        <label style={layout.label}>City, State and ZIP code *</label>
        <input type="text" value={data.city} onChange={(e) => update({ city: e.target.value })} style={layout.input} />
      </div>

      <div style={{ marginBottom: 20 }}>
        <label style={layout.label}>Filing Status *</label>
        {filingOptions.map((opt) => (
          <div
            key={opt.value}
            onClick={() => update({ married: opt.value })}
            style={{
              ...layout.optionCard,
              ...(data.married === opt.value ? layout.optionCardSelected : {}),
            }}
          >
            <div
              style={{
                width: 18,
                height: 18,
                borderRadius: '50%',
                border: `2px solid ${data.married === opt.value ? colors.accent : '#9CA3AF'}`,
                position: 'relative',
                flexShrink: 0,
              }}
            >
              {data.married === opt.value && (
                <div
                  style={{
                    position: 'absolute',
                    top: 2,
                    left: 2,
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    background: colors.accent,
                  }}
                />
              )}
            </div>
            {opt.label}
          </div>
        ))}
      </div>

      <h4 style={{ fontSize: 15, fontWeight: 700, margin: '16px 0 10px' }}>Step 3 — Claim Dependents</h4>
      <div style={inputRow}>
        <div>
          <label style={layout.label}>{childAmountLabel}</label>
          <input
            type="text"
            inputMode="decimal"
            value={getAmount(data.amounts, 9)}
            onChange={(e) => updateAmount(9, e.target.value)}
            placeholder="$0"
            style={layout.input}
          />
        </div>
        <div>
          <label style={layout.label}>Multiply other dependents by $500</label>
          <input
            type="text"
            inputMode="decimal"
            value={getAmount(data.amounts, 10)}
            onChange={(e) => updateAmount(10, e.target.value)}
            placeholder="$0"
            style={layout.input}
          />
        </div>
      </div>
      <div style={{ marginBottom: 16 }}>
        <label style={layout.label}>Step 3 — Total</label>
        <input
          type="number"
          {...selectAllOnFocus}
          value={getAmount(data.amounts, 11)}
          onChange={(e) => updateAmount(11, e.target.value)}
          placeholder="$0"
          style={layout.input}
        />
      </div>

      <h4 style={{ fontSize: 15, fontWeight: 700, margin: '16px 0 10px' }}>Step 4 — Other Adjustments</h4>
      <div style={inputRow}>
        <div>
          <label style={layout.label}>4(a) — Other income (not from jobs)</label>
          <input
            type="text"
            inputMode="decimal"
            value={getAmount(data.amounts, 12)}
            onChange={(e) => updateAmount(12, e.target.value)}
            placeholder="$0"
            style={layout.input}
          />
        </div>
        <div>
          <label style={layout.label}>4(b) — Deductions</label>
          <input
            type="text"
            inputMode="decimal"
            value={getAmount(data.amounts, 13)}
            onChange={(e) => updateAmount(13, e.target.value)}
            placeholder="$0"
            style={layout.input}
          />
        </div>
      </div>
      <div style={{ marginBottom: 20 }}>
        <label style={layout.label}>4(c) — Extra withholding per pay period</label>
        <input
          type="number"
          {...selectAllOnFocus}
          value={getAmount(data.amounts, 14)}
          onChange={(e) => updateAmount(14, e.target.value)}
          placeholder="$0"
          style={layout.input}
        />
      </div>

      <h4 style={{ fontSize: 15, fontWeight: 700, margin: '16px 0 10px' }}>Step 5 — Employer Information</h4>
      <div style={{ marginBottom: 16 }}>
        <label style={layout.label}>Employer&apos;s name and address</label>
        <input type="text" value={employerName} disabled style={{ ...layout.input, background: '#E5E7EB' }} />
      </div>
      <div style={inputRow}>
        <div>
          <label style={layout.label}>First date of employment</label>
          <DateInput value={data.date} onChange={(v) => update({ date: v })} />
        </div>
        <div>
          <label style={layout.label}>Employer Identification Number (EIN)</label>
          <input type="text" value={ein} disabled style={{ ...layout.input, background: '#E5E7EB' }} />
        </div>
      </div>

      <SignatureBottomSheet
        isOpen={signatureOpen}
        onClose={() => setSignatureOpen(false)}
        onSubmit={submitWithSignature}
        title="Sign W-4"
        submitLabel="Accept & Sign"
        previewPdfUrl={livePdfUrl}
      />
    </StepShell>
  );
};

export default W4Step;
